import React, { useState, useMemo, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCourses, courseKeys } from '@/hooks/useCourses';
import { useLessonProgress } from '@/hooks/useLessonProgress';
import { useSubscriptionContext } from '@/contexts/SubscriptionContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useDeleteCourse, adminCourseKeys } from '@/hooks/useAdminCourses';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/hooks/use-toast';
import { CourseCard } from './CourseCard';
import { getCourseProgress, getModuleLessonCounts } from '@/types/courses';
import type { Course } from '@/types/courses';
import { Search, BookOpen, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { UpgradePrompt } from '@/components/upgrade/UpgradePrompt';
import { createPortal } from 'react-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Lazy-loaded admin form — only fetched when an admin opens the create/edit sheet.
// Keeps CreateCourse out of the main CourseLibrary chunk for non-admin users.
const LazyCreateCourse = React.lazy(() => import('@/components/admin/CreateCourse'));

interface CourseLibraryProps {
  courseType?: 'standard' | 'nextgen';
  basePath: string;
}

export function CourseLibrary({ courseType, basePath }: CourseLibraryProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthResolved } = useAuth();
  const { data: userRole } = useUserRole();
  const { data: courses, isLoading, error } = useCourses({ courseType });

  const isAdmin = userRole?.canEditCourses === true;
  const editMode = isAdmin && searchParams.get('edit') === '1';

  const exitEditMode = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  };

  const { data: completedLessons = {} } = useLessonProgress();
  const { isActive, tierName, isLoading: subLoading } = useSubscriptionContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState<string | null>(null);
  const [isCreatingCourse, setIsCreatingCourse] = useState(false);
  const [deletingCourse, setDeletingCourse] = useState<Course | null>(null);
  const { toast } = useToast();
  const deleteCourse = useDeleteCourse();
  const queryClient = useQueryClient();

  // CourseLibrary is mounted from /coursework or /nextgen/coursework.
  // Use the basePath to decide which course_type new courses default to.
  const isNextGen = basePath.startsWith('/nextgen');
  const sheetOpen = editMode && (isCreatingCourse || editingCourseId !== null);

  const closeSheet = () => {
    setIsCreatingCourse(false);
    setEditingCourseId(null);
  };

  const handleSheetSuccess = () => {
    closeSheet();
    // CreateCourse writes via raw supabase, so it does NOT auto-invalidate
    // useCourses / useAdminCourses caches. Invalidate manually so the library refreshes.
    queryClient.invalidateQueries({ queryKey: courseKeys.all });
    queryClient.invalidateQueries({ queryKey: adminCourseKeys.all });
  };

  const isSubscribed = isActive && tierName !== 'Free';

  const filteredCourses = useMemo(() => {
    if (!courses) return [];
    // In edit mode, show all statuses (including Draft + Archived). Otherwise hide them.
    let filtered = editMode
      ? [...courses]
      : courses.filter(
          (c) =>
            c.status?.toLowerCase() !== 'archived' &&
            c.status?.toLowerCase() !== 'draft'
        );
    // Search filter
    const q = searchQuery.toLowerCase().trim();
    if (q) {
      filtered = filtered.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.tile_description?.toLowerCase().includes(q) ||
          c.overview?.toLowerCase().includes(q)
      );
    }
    // Sort: featured first, then alphabetical
    return [...filtered].sort((a, b) => {
      const aFeatured = a.is_featured && a.status !== 'Coming Soon';
      const bFeatured = b.is_featured && b.status !== 'Coming Soon';
      if (aFeatured && !bFeatured) return -1;
      if (!aFeatured && bFeatured) return 1;

      const aComingSoon = a.status === 'Coming Soon';
      const bComingSoon = b.status === 'Coming Soon';
      if (!aComingSoon && bComingSoon) return -1;
      if (aComingSoon && !bComingSoon) return 1;

      return a.title.localeCompare(b.title);
    });
  }, [courses, searchQuery, editMode]);

  const handleSelect = (courseId: string) => {
    if (!isSubscribed && !subLoading) {
      setShowUpgrade(true);
      return;
    }
    navigate(`${basePath}/${courseId}`);
  };

  if (error) {
    return (
      <div className="text-center py-20">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 max-w-lg mx-auto">
          <h3 className="text-lg font-medium text-red-800 dark:text-red-300 mb-2">
            Failed to load courses
          </h3>
          <p className="text-sm text-red-600 dark:text-red-400 font-mono">
            {(error as Error).message || String(error)}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="space-y-2">
            <div className="h-7 w-40 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
          <div className="h-10 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* pb-[calc(5rem+env(safe-area-inset-bottom))] on the page root keeps the */}
      {/* last course card from sitting under the mobile bottom nav. Mirrors the */}
      {/* convention used by every other NextGen page (NextGenSkillsPage, etc.) */}
      <div className="pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0">
        {editMode && (
          <div className="sticky top-0 z-30 -mx-4 px-4 mb-4 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800/60 text-amber-900 dark:text-amber-200 flex items-center justify-between text-sm h-10">
            <span>Editing as admin · changes are live</span>
            <button
              type="button"
              onClick={exitEditMode}
              className="font-medium hover:underline focus:outline-none focus:underline"
            >
              Exit
            </button>
          </div>
        )}
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Course Library
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Select a subject to begin studying.
            </p>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-8 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F37022]/20 focus:border-[#F37022]"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1.5 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {editMode && (
              <Button
                type="button"
                onClick={() => setIsCreatingCourse(true)}
                className="bg-[#F37022] hover:bg-[#F37022]/90 text-white whitespace-nowrap"
              >
                <Plus className="w-4 h-4 mr-1" />
                New {isNextGen ? 'NextGen ' : ''}Course
              </Button>
            )}
          </div>
        </div>

        {/* Grid */}
        {filteredCourses.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredCourses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                progress={getCourseProgress(course, completedLessons)}
                counts={getModuleLessonCounts(course)}
                isSubscribed={isSubscribed}
                onSelect={handleSelect}
                editMode={editMode}
                onEdit={editMode ? (id) => setEditingCourseId(id) : undefined}
                onDelete={editMode ? (c) => setDeletingCourse(c) : undefined}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
              {searchQuery ? (
                <Search className="h-8 w-8 text-gray-400" />
              ) : (
                <BookOpen className="h-8 w-8 text-gray-400" />
              )}
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {searchQuery ? 'No courses found' : 'No Courses Yet'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              {searchQuery
                ? 'Try searching for a different keyword.'
                : 'Courses are being prepared. Check back soon.'}
            </p>
            {searchQuery && (
              <Button
                variant="link"
                onClick={() => setSearchQuery('')}
                className="mt-2 text-[#F37022]"
              >
                Clear search
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Upgrade prompt modal for free tier users */}
      {showUpgrade &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[10000]"
            onClick={() => setShowUpgrade(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <UpgradePrompt
                feature="courses"
                variant="modal"
                onUpgradeClick={() => {
                  setShowUpgrade(false);
                  navigate('/pricing');
                }}
              />
            </div>
          </div>,
          document.body
        )}

      {editMode && (
        <Sheet
          open={sheetOpen}
          onOpenChange={(open) => {
            if (!open) closeSheet();
          }}
        >
          <SheetContent className="sm:max-w-md md:max-w-xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle>
                {editingCourseId ? 'Edit Course' : `Create ${isNextGen ? 'NextGen ' : ''}Course`}
              </SheetTitle>
              <SheetDescription>
                {editingCourseId
                  ? 'Update the course details below'
                  : `Create a new ${isNextGen ? 'NextGen ' : ''}course`}
              </SheetDescription>
            </SheetHeader>
            <Suspense
              fallback={
                <div className="p-6 flex justify-center">
                  <LoadingSpinner className="h-8 w-8" />
                </div>
              }
            >
              {sheetOpen && (
                <LazyCreateCourse
                  editMode={!!editingCourseId}
                  courseId={editingCourseId ?? undefined}
                  defaultCourseType={isNextGen ? 'nextgen' : 'standard'}
                  onClose={closeSheet}
                  onSuccess={handleSheetSuccess}
                />
              )}
            </Suspense>
          </SheetContent>
        </Sheet>
      )}

      {editMode && (
        <AlertDialog
          open={!!deletingCourse}
          onOpenChange={(open) => {
            if (!open) setDeletingCourse(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this course?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{deletingCourse?.title}"? This will also
                delete all associated modules and lessons. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteCourse.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={deleteCourse.isPending}
                onClick={async (e) => {
                  e.preventDefault();
                  if (!deletingCourse) return;
                  try {
                    await deleteCourse.mutateAsync(deletingCourse.id);
                    toast({
                      title: 'Course deleted',
                      description: `"${deletingCourse.title}" was deleted.`,
                    });
                    setDeletingCourse(null);
                  } catch (err) {
                    toast({
                      title: 'Delete failed',
                      description: (err as Error).message || 'Could not delete course.',
                      variant: 'destructive',
                    });
                  }
                }}
                className="bg-destructive hover:bg-destructive/90"
              >
                {deleteCourse.isPending ? 'Deleting…' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
